<template>
  <div class="shop-details-form">
    <div class="shop-details-form__general-informations">
      <span class="sk-text-large-semibold">
        {{ $t('create_shop.general_informations') }}
      </span>
      <div class="shop-details-form__general-informations__inputs-grid">
        <div>
          <SkInput
            ref="nameInput"
            v-model.trim="name"
            type="text"
            :label="$t('create_shop.shop_name_input')"
            :errored="!!nameErrorMessage"
            :error-message="nameErrorMessage"
            :debounce="handleNameChanges"
            :debounce-ms="300"
          />
          <SkDropdown
            ref="companyNameDropdown"
            placement="bottom-start"
            y-offset="10px"
            :trigger="null"
          >
            <template #menu>
              <SuggestedCompanyRegistrations
                v-if="showCompanyNameDropdown"
                :suggested-companies="suggestedCompanyRegistrations"
                :search-input="name"
                :related-input="$refs.nameInput"
                @select-suggested-company="autofillBillingInfos"
              />
            </template>
          </SkDropdown>
        </div>
        <div>
          <SkInput
            ref="registrationNumberInput"
            v-model.trim="registrationNumber"
            type="text"
            :label="$t('fill_billing_info.billing_line.shop_registration_number_input')"
            :errored="!!registrationNumberErrorMessage"
            :error-message="registrationNumberErrorMessage"
            :debounce="handleAndValidateRegistrationNumber"
            :debounce-ms="300"
          />
          <SkDropdown
            ref="registrationNumberDropdown"
            placement="bottom-start"
            y-offset="10px"
            :trigger="null"
          >
            <template #menu>
              <SuggestedCompanyRegistrations
                v-if="showRegistrationNumberDropdown"
                :suggested-companies="suggestedCompanyRegistrations"
                :search-input="registrationNumber"
                :related-input="$refs.registrationNumberInput"
                @select-suggested-company="autofillBillingInfos"
              />
            </template>
          </SkDropdown>
        </div>
        <template v-if="showNewPackOfferFields">
          <SkInput
            ref="maxUsersInput"
            v-model.trim="maxUsers"
            type="number"
            :label="$t('create_shop.max_users')"
            :errored="!!maxUsersErrorMessage"
            :error-message="maxUsersErrorMessage"
            :debounce="validateMaxUsers()"
            :debounce-ms="300"
          />
          <SkInput
            v-model.trim="paymentFrequency"
            :label="$t('create_shop.payment_frequency.title')"
            disabled
          />
        </template>
        <div
          v-if="showAnnouncedUserField"
          class="shop-details-form__announced-users-field"
        >
          <SkInput
            v-model="announcedUsers"
            type="number"
            min="0"
            :label="$t('create_shop.announced_users')"
            :debounce-ms="300"
            :debounce="updateAnnouncedUsers()"
          />
          <span class="sk-text-x-small-regular">{{ $t('create_shop.shop_employees_count') }}</span>
        </div>
      </div>
    </div>
    <div class="shop-details-form__billing-address">
      <span class="sk-text-large-semibold">
        {{ billingAddressLabel }}
      </span>
      <div class="shop-details-form__billing-address__inputs-grid">
        <SkInput
          v-model.trim="address"
          class="shop-details-form__billing-address__inputs-grid__address"
          type="text"
          :label="$t('fill_billing_info.billing_line.address_input')"
          :errored="!!addressErrorMessage"
          :error-message="addressErrorMessage"
          :debounce="validateMandatoryField('address')"
          :debounce-ms="100"
        />
        <SkInput
          v-model.trim="zipcode"
          type="text"
          :label="$t('fill_billing_info.billing_line.zipcode_input')"
          :errored="!!zipcodeErrorMessage"
          :error-message="zipcodeErrorMessage"
          :debounce="validateMandatoryField('zipcode')"
          :debounce-ms="100"
        />
        <SkInput
          v-model.trim="city"
          type="text"
          :label="$t('fill_billing_info.billing_line.city_input')"
          :errored="!!cityErrorMessage"
          :error-message="cityErrorMessage"
          :debounce="validateMandatoryField('city')"
          :debounce-ms="100"
        />
        <SkSelectV2
          v-model.trim="country"
          :options="countryOptions"
          :label="$t('fill_billing_info.billing_line.country_input')"
          :errored="!!countryErrorMessage"
          :error-message="countryErrorMessage"
          :debounce="validateMandatoryField('country')"
          :debounce-ms="100"
          :disabled="!isAllowedToUpdateCountryField"
        />
        <SkInput
          v-if="showSdiField"
          v-model.trim="sdi"
          class="shop-details-form__billing-address__inputs-grid__sdi"
          type="text"
          :label="$t('fill_billing_info.billing_line.sdi_input')"
          :errored="!!sdiErrorMessage"
          :error-message="sdiErrorMessage"
        />
      </div>
    </div>
    <div
      v-if="!isBillingOnOrganisation"
      class="shop-details-form__payment-informations"
    >
      <div class="shop-details-form__payment-informations__header">
        <span class="sk-text-large-semibold">
          {{ $t('create_shop.payment_informations') }}
        </span>
        <SkOroraTag
          v-if="hasAddedPayment && displayPaymentByCreditCard"
          variant="green"
        >
          {{ $t('create_shop.payment_added_tag') }}
        </SkOroraTag>
      </div>

      <div
        v-if="displayPaymentByBankTransfer"
        class="shop-details-form__payment-informations__bank-transfer"
      >
        <span class="shop-details-form__payment-informations__bank-label sk-text-medium-regular">
          {{ $t('fill_billing_info.billing_line.bank_transfer.payment_method_label') }}
          <span class="sk-text-medium-semibold">
            {{ $t('fill_billing_info.billing_line.bank_transfer.payment_method_span') }}
          </span>
        </span>
        <SkInput
          v-model.trim="email"
          type="text"
          :label="$t('fill_billing_info.billing_line.email_input')"
          :errored="!!emailErrorMessage"
          :error-message="emailErrorMessage"
          :debounce="validateEmail"
          :debounce-ms="100"
        />
        <span class="sk-text-medium-regular">
          {{ $t('fill_billing_info.billing_line.bank_transfer.payment_method_description') }}
        </span>
      </div>

      <div
        v-else
        class="shop-details-form__payment-informations__payment-method"
      >
        <SkOroraButton
          v-if="displayPaymentByCreditCard"
          variant="secondary"
          @click="openCheckout"
        >
          {{ paymentButtonLabel }}
        </SkOroraButton>

        <div
          v-else
          class="shop-details-form__payment-informations__iban"
        >
          <SkInput
            v-if="displayPaymentByIban"
            v-model.trim="ibanWithSpacesAllowed"
            type="text"
            :label="$t('fill_billing_info.billing_line.iban_input')"
            :errored="!!ibanErrorMessage"
            :error-message="ibanErrorMessage"
            :debounce="validateIban"
            :debounce-ms="100"
          />
          <SkInput
            v-model.trim="email"
            type="text"
            :label="$t('fill_billing_info.billing_line.email_input')"
            :errored="!!emailErrorMessage"
            :error-message="emailErrorMessage"
            :debounce="validateEmail"
            :debounce-ms="100"
          />
        </div>

        <div class="shop-details-form__secure-payment-label">
          <ShieldWithCheckIcon
            :fill="$skColors.skBlue50"
            width="16"
            height="16"
          />
          <span class="sk-text-medium-semibold">
            {{ $t('create_shop.secure_payment_label') }}
          </span>
        </div>
      </div>
    </div>
    <UpsellBanner
      v-if="showPriceIncreaseBanner"
      :description="upsellBannerDescription"
    />
  </div>
</template>

<script>
import { captureException } from '@sentry/vue';
import {
  mapGetters,
  mapState,
  mapActions,
} from 'vuex';

import UpsellBanner from '@components/UpsellBanner';
import SuggestedCompanyRegistrations from '@components/BillingLineContent/SuggestedCompanyRegistrations';
import dayjs from 'dayjs';
import { QuoteLineItemType } from '@skelloapp/svc-billing-automation-sdk';
import { billingAutomationClient } from '@/shared/utils/clients/billingAutomationClient';
import {
  removeWhiteSpaces,
  resolveMaxUsersRangeForProduct,
  extractPrice,
  convertToSnakeCase,
} from '@/shared/utils/method_helper';
import {
  isValidSiret,
  isValidIban,
  isValidEmail,
} from '@/shared/utils/validators';
import { VUE_APP_CHARGEBEE_SITE } from '@/shared/config';
import { getNaf } from '@/shared/utils/naf';
import { COUNTRY_KEYS } from '@/shared/constants/country-keys';

const MAX_USERS_ALLOWED = 1000;

export default {
  name: 'ShopDetailsForm',
  components: { SuggestedCompanyRegistrations, UpsellBanner },
  props: {
    shopPaymentInfos: {
      type: Object,
      required: true,
    },
    lineItemsInfos: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },
  data() {
    return {
      registrationNumberWithSpacesAllowed: null,
      addressErrorMessage: null,
      cityErrorMessage: null,
      zipcodeErrorMessage: null,
      countryErrorMessage: null,
      ibanErrorMessage: null,
      emailErrorMessage: null,
      suggestedCompanyRegistrations: [],
      nameErrorMessage: null,
      registrationNumberErrorMessage: null,
      maxUsersErrorMessage: null,
      sdiDirty: false,
      isDropDownOpen: {
        companyName: false,
        registrationNumber: false,
      },
      cbInstance: window.Chargebee.init({ site: VUE_APP_CHARGEBEE_SITE }),
      hasAddedPayment: false,
      announcedUsers: null,
    };
  },
  computed: {
    ...mapGetters([
      'isDevFeatureFlagActive',
      'displayPaymentByBankTransfer',
      'displayPaymentByCreditCard',
      'isBillingOnOrganisation',
      'isContractChangeFromSalesforce',
      'isCatalogVersionV2',
      'isContractChangeFromInAppAndMassMarketCustomer',
      'isNewOffer2026',
      'isBillingSplitByShopEmployees',
      'isBillingOnShop',
      'isItalianBillingEntity',
    ]),
    ...mapState(['quote']),
    isAllowedToUpdateCountryField() {
      return this.isDevFeatureFlagActive('FEATUREDEV_INAPP_ROLLOUT_RAUL_COUNTRY') && !this.isContractChangeFromSalesforce;
    },
    countryOptions() {
      return COUNTRY_KEYS.map(country => ({ id: country, text: this.$t(`common.countries.${country}`) }));
    },
    isSubmitDisabled() {
      const isPaymentValid = this.hasAddedPayment ||
        (this.displayPaymentByIban && !!this.iban && !!this.email) ||
        (this.displayPaymentByBankTransfer && !!this.email);
      const isFilled =
        this.address && this.city && this.zipcode && this.country &&
        this.registrationNumber && this.name && this.newFormFieldsAreFilled &&
        this.announcedUsersFieldFilled;
      const sdiInvalid = this.showSdiField && (!!this.sdiErrorMessage || !this.sdi);
      const hasAnyErrorMessage = !!this.addressErrorMessage || !!this.nameErrorMessage ||
        !!this.cityErrorMessage || !!this.zipcodeErrorMessage || !!this.countryErrorMessage ||
        !!this.registrationNumberErrorMessage || !!this.ibanErrorMessage ||
        !!this.emailErrorMessage || sdiInvalid || this.newFormFieldsHaveError;

      return hasAnyErrorMessage || !isFilled || !isPaymentValid;
    },
    newFormFieldsAreFilled() {
      return this.showNewPackOfferFields ? (this.maxUsers && this.commitment) : true;
    },
    announcedUsersFieldFilled() {
      return this.showAnnouncedUserField ? !!this.announcedUsers : true;
    },
    newFormFieldsHaveError() {
      return this.showNewPackOfferFields ? !!this.maxUsersErrorMessage : false;
    },
    // Only INSEE API is implemented to look for company registrations for the moment.
    isSupportedCountryForRegistrationLookup() {
      return this.shopPaymentInfos.billingAddress.country_code === 'FR';
    },
    name: {
      get() {
        // Raul SHOULD send only one shop in the quote for contract change
        // this is checked in populatePaymentInfos()
        return this.shopPaymentInfos.name;
      },
      set(value) {
        this.validatePresence('name');
        this.updatePaymentInfosAttribute({
          attribute: 'name',
          value,
          index: this.shopPaymentInfos.salesforceId,
        });
      },
    },
    address: {
      get() {
        // Raul SHOULD send only one shop in the quote for contract change
        // this is checked in populatePaymentInfos()
        return this.shopPaymentInfos.billingAddress.line1;
      },
      set(value) {
        this.validatePresence('line1');
        this.updatePaymentInfosAttribute({
          attribute: 'billingAddress',
          value: { ...this.shopPaymentInfos.billingAddress, line1: value },
          index: this.shopPaymentInfos.salesforceId,
        });
      },
    },
    city: {
      get() {
        // Raul SHOULD send only one shop in the quote for contract change
        // this is checked in populatePaymentInfos()
        return this.shopPaymentInfos.billingAddress.city;
      },
      set(value) {
        this.validatePresence('city');
        this.updatePaymentInfosAttribute({
          attribute: 'billingAddress',
          value: { ...this.shopPaymentInfos.billingAddress, city: value },
          index: this.shopPaymentInfos.salesforceId,
        });
      },
    },
    country: {
      get() {
        // Raul SHOULD send only one shop in the quote for contract change
        // this is checked in populatePaymentInfos()
        return this.shopPaymentInfos.billingAddress.country_code;
      },
      set(value) {
        this.validatePresence('country');
        this.updatePaymentInfosAttribute({
          attribute: 'billingAddress',
          value: {
            ...this.shopPaymentInfos.billingAddress,
            country_code: value,
            country: this.$t(`common.countries.${value}`, 'en'), // Raul expects an english country's value
          },
          index: this.shopPaymentInfos.salesforceId,
        });

        // since the registration number is depending on the country, we have to run again the validation
        this.validateRegistrationNumber();
      },
    },
    zipcode: {
      get() {
        // Raul SHOULD send only one shop in the quote for contract change
        // this is checked in populatePaymentInfos()
        return this.shopPaymentInfos.billingAddress.zip;
      },
      set(value) {
        this.validatePresence('zip');
        this.updatePaymentInfosAttribute({
          attribute: 'billingAddress',
          value: { ...this.shopPaymentInfos.billingAddress, zip: value },
          index: this.shopPaymentInfos.salesforceId,
        });
      },
    },
    ibanWithSpacesAllowed: {
      get() {
        return this.shopPaymentInfos.iban || null;
      },
      set(value) {
        this.updatePaymentInfosAttribute({
          attribute: 'iban',
          value,
          index: this.shopPaymentInfos.salesforceId,
        });
      },
    },
    email: {
      get() {
        return this.shopPaymentInfos.billingAddress.email || null;
      },
      set(value) {
        this.updatePaymentInfosAttribute({
          attribute: 'billingAddress',
          value: { ...this.shopPaymentInfos.billingAddress, email: value },
          index: this.shopPaymentInfos.salesforceId,
        });
      },
    },
    registrationNumber: {
      get() {
        return this.registrationNumberWithSpacesAllowed &&
          removeWhiteSpaces(this.registrationNumberWithSpacesAllowed);
      },
      set(value) {
        this.validateRegistrationNumber();
        this.registrationNumberWithSpacesAllowed = value;
        this.updatePaymentInfosAttribute({
          attribute: 'registrationNumber',
          value: this.registrationNumber,
          index: this.shopPaymentInfos.salesforceId,
        });
      },
    },
    paymentFrequency() {
      const paymentFrequency = this.shopPaymentInfos.contractTerms.payment_frequency || 'Yearly';
      return this.$t(`create_shop.payment_frequency.${convertToSnakeCase(paymentFrequency)}`);
    },
    maxUsers: {
      get() {
        return this.lineItemsInfos.maxUsers || null;
      },
      set(value) {
        this.validateMaxUsers();
        this.updateLineItemsInfosAttribute({
          attribute: 'maxUsers',
          value,
          shopId: this.shopPaymentInfos.salesforceId,
        });
      },
    },
    commitment() {
      return this.shopPaymentInfos.contractTerms.commitment || 'Yearly';
    },
    showCompanyNameDropdown() {
      return this.suggestedCompanyRegistrations.length > 0 &&
        this.isDropDownOpen.companyName;
    },
    showRegistrationNumberDropdown() {
      return this.suggestedCompanyRegistrations.length > 0 &&
        this.isDropDownOpen.registrationNumber;
    },
    billingAddressLabel() {
      return this.isBillingOnOrganisation ?
        this.$t('create_shop.billing_address_label.billing_on_orga') :
        this.$t('create_shop.billing_address_label.billing_on_shop');
    },
    chargebeeId() {
      return this.shopPaymentInfos.paymentOwnerChargebeeId;
    },
    paymentButtonLabel() {
      return this.hasAddedPayment ? this.$t('create_shop.payment_button.modify_payment') : this.$t('create_shop.payment_button.add_payment');
    },
    displayPaymentByIban() {
      return this.quote.payment_method === 'Direct debit';
    },
    iban() {
      return this.ibanWithSpacesAllowed && removeWhiteSpaces(this.ibanWithSpacesAllowed);
    },
    getQuoteLineItemsWorkspace() {
      if (this.isNewOffer2026) {
        return this.quote.quote_line_items?.find(item => item.type === QuoteLineItemType.WORKSPACE);
      }
      return null;
    },
    getShopPaymentFrequency() {
      return this.quote.organisation.shops?.[0]?.contract_terms?.payment_frequency;
    },
    quoteTotalBannerDescription() {
      const totalAmount = this.quote.totals?.total_amount;
      const engagementUnit = this.quote.contract_terms.total_engagement_unit;
      if (typeof totalAmount !== 'number' || !engagementUnit) {
        return null;
      }

      const options = this.quote.quote_line_items
        .filter(item => item.type?.includes('Option'))
        .map(item => item.title);
      const paymentKey = this.quote.type_of_payment === 'Shop' ? 'shop' : 'organisation';

      return this.$tc(`upsell_banner_${engagementUnit}.${paymentKey}_payment_description`, options.length, {
        start_date: this.newPackDate,
        pack_name: this.quote.quote_line_items.find(item => item.type === 'Pack')?.title,
        options: options.length === 1 ? options[0] : options.slice(0, -1).join(', '),
        last_option: options[options.length - 1],
        price: extractPrice(this.quote, totalAmount),
      });
    },
    upsellBannerDescription() {
      if (!this.isNewOffer2026 && !this.showNewPackOfferFields) {
        return this.quoteTotalBannerDescription;
      }

      const startDate = this.newPackDate;
      const workspacePrice = this.getQuoteLineItemsWorkspace?.unit_amount;
      const shopPaymentFrequency = this.getShopPaymentFrequency ?
        convertToSnakeCase(this.getShopPaymentFrequency) :
        null;
      if (this.isNewOffer2026 && workspacePrice >= 0 && shopPaymentFrequency) {
        return this.$t('new_pack_offer.upsell_banner.pricing_unique', {
          start_date: startDate,
          price: extractPrice(this.quote, workspacePrice),
          payment_frequency: this.$t(`quote_details.billing_cycle.${shopPaymentFrequency}`),
        });
      }

      const packName = this.quote.quote_line_items[0].title;
      const maxUsers =
        resolveMaxUsersRangeForProduct(this.quote, this.lineItemsInfos.title, this.maxUsers);
      const price = this.resolvePrice(maxUsers);
      const option = this.quote.quote_line_items.find(i => i.type === 'Option')?.title;
      const translationOptions = {
        start_date: startDate,
        pack_name: packName,
        max_users: maxUsers,
        price,
        option_name: option,
      };

      const translation = option ?
        'new_pack_offer.upsell_banner.payment_description_with_option' :
        'new_pack_offer.upsell_banner.payment_description';

      return this.$tc(translation, 0, translationOptions);
    },
    showNewPackOfferFields() {
      if (this.isNewOffer2026) return false;

      return this.isCatalogVersionV2 && this.isContractChangeFromInAppAndMassMarketCustomer &&
        this.isDevFeatureFlagActive('FEATUREDEV_INAPP_ROLLOUT_DISPLAY_NEW_FIELDS');
    },
    showAnnouncedUserField() {
      return this.isBillingSplitByShopEmployees;
    },
    showPriceIncreaseBanner() {
      if (this.showNewPackOfferFields || this.isNewOffer2026) {
        return true;
      }

      return !this.isContractChangeFromSalesforce && !!this.quoteTotalBannerDescription;
    },
    newPackDate() {
      return dayjs(this.quote.contract_terms.contract_term_start).locale(this.$i18n.locale).format('DD MMMM YYYY');
    },
    sdi: {
      get() {
        return this.shopPaymentInfos.sdi;
      },
      set(value) {
        this.sdiDirty = true;
        this.updatePaymentInfosAttribute({
          attribute: 'sdi',
          value,
          index: this.shopPaymentInfos.salesforceId,
        });
      },
    },
    sdiErrorMessage() {
      if (!this.showSdiField) return null;
      if (!this.sdiDirty) return null;
      if (!this.sdi) return this.$t('fill_billing_info.billing_line.error_input_mandatory');
      if (!/^[a-zA-Z0-9]{6,7}$/.test(this.sdi)) return this.$t('fill_billing_info.billing_line.sdi_invalid');
      return null;
    },
    showSdiField() {
      return this.isBillingOnShop && this.isItalianBillingEntity;
    },
  },
  watch: {
    isSubmitDisabled(newValue) {
      this.$emit('toggle-submit', newValue);
    },
  },
  mounted() {
    if (!this.isContractChangeFromSalesforce) this.$emit('toggle-submit', true);
    this.registrationNumber = this.shopPaymentInfos.registrationNumber || null;
    this.address = this.shopPaymentInfos.billingAddress?.line1 || null;
    this.city = this.shopPaymentInfos.billingAddress?.city || null;
    this.zipcode = this.shopPaymentInfos.billingAddress?.zip || null;
    this.country = this.shopPaymentInfos.billingAddress?.country_code || null;

    this.hasAddedPayment = this.shopPaymentInfos.hasValidPaymentMethod ||
      this.isBillingOnOrganisation;

    this.announcedUsers = this.quote.split_line_items?.[0]?.announced_users ||
      null;
  },
  methods: {
    ...mapActions([
      'updateSplitLineItems',
      'updatePaymentInfosAttribute',
      'updateLineItemsInfosAttribute',
    ]),

    validateMandatoryField(attribute) {
      const value = this[attribute];

      if (value === '') {
        this[`${attribute}ErrorMessage`] = this.$t('fill_billing_info.billing_line.error_input_mandatory');
      } else {
        this[`${attribute}ErrorMessage`] = null;
      }
    },
    // for the companyName "L'AMI DU PAIN" with 'AMI D' as input, we return the array:
    // [{ text: "L'" },
    //  { text: 'AMI D', isHighlighted: true },
    //  { text: 'U PAIN' }]
    //
    highlightMatch(result, searchedText) {
      const matching = result.toLowerCase().match(searchedText.toLowerCase());
      if (!matching) return [{ text: result }];

      const formattedText = [
        { text: searchedText, isHighlighted: true },
        { text: result.substring(matching.index + searchedText.length) },
      ];

      if (matching.index > 0) {
        formattedText.unshift({ text: result.substring(0, matching.index) });
      }

      return formattedText;
    },
    clearSuggestedCompanyRegistrations() {
      this.suggestedCompanyRegistrations = [];
      this.isDropDownOpen.registrationNumber = false;
      this.isDropDownOpen.companyName = false;
      this.$refs.companyNameDropdown.hide();
      this.$refs.registrationNumberDropdown.hide();
    },
    handleNameChanges() {
      this.validatePresence('name');
      if (!this.isSupportedCountryForRegistrationLookup) return;
      if (this.name.length < 3) {
        this.clearSuggestedCompanyRegistrations();
        return;
      }

      this.lookupCompanyRegistrationsByName()
        .then(response => {
          this.populateSuggestedCompanyRegistrations(response, this.name);
          this.isDropDownOpen.companyName = true;
          this.isDropDownOpen.registrationNumber = false;
          this.$refs.companyNameDropdown.open();
          this.$refs.registrationNumberDropdown.hide();
        }).catch(this.clearSuggestedCompanyRegistrations);
    },
    updateAnnouncedUsers() {
      this.updateSplitLineItems({
        index: 0,
        updateSplitLineItem: {
          announced_users: this.announcedUsers,
        },
      });
    },
    populateSuggestedCompanyRegistrations(suggestedCompanies, searchedText) {
      this.suggestedCompanyRegistrations = suggestedCompanies.map(suggestedCompany => ({
        address: suggestedCompany.address,
        city: suggestedCompany.city,
        creationDate: suggestedCompany.creationDate,
        shopRegistrationNumber: suggestedCompany.shopRegistrationNumber,
        displayLabel: this.highlightMatch(
          suggestedCompany.name,
          searchedText,
        ),
        // We need to remove the dot in the main activity to be able to use the naf filter
        // Dots are added by the INSEE API
        mainActivity: getNaf(suggestedCompany.mainActivity.replaceAll('.', '')),
        name: suggestedCompany.name,
        organisationRegistrationNumber: suggestedCompany.organisationRegistrationNumber,
        zipCode: suggestedCompany.zipCode,
      }));
    },
    handleAndValidateRegistrationNumber() {
      this.handleRegistrationNumberInput();
      this.validateRegistrationNumber();
    },
    handleRegistrationNumberInput() {
      if (!this.isSupportedCountryForRegistrationLookup) return;
      if (this.registrationNumber.length < 3) {
        this.clearSuggestedCompanyRegistrations();
        return;
      }

      this.lookupCompanyRegistrationsByRegistrationNumber()
        .then(response => {
          this.populateSuggestedCompanyRegistrations(response, '');
          this.isDropDownOpen.registrationNumber = true;
          this.isDropDownOpen.companyName = false;
          this.$refs.registrationNumberDropdown.open();
          this.$refs.companyNameDropdown.hide();
        }).catch(this.clearSuggestedCompanyRegistrations);
    },
    validateRegistrationNumber() {
      this.validatePresence('registrationNumber');
      if (!this.registrationNumber || !this.isSupportedCountryForRegistrationLookup) return;

      if (!isValidSiret(this.registrationNumber)) {
        this.registrationNumberErrorMessage = this.$t('fill_billing_info.billing_line.siret_invalid');
      }
    },
    validatePresence(attribute) {
      const value = this[attribute];
      if (value === '') {
        this[`${attribute}ErrorMessage`] = this.$t('fill_billing_info.billing_line.error_input_mandatory');
      } else {
        this[`${attribute}ErrorMessage`] = null;
      }
    },
    lookupCompanyRegistrationsByName() {
      return billingAutomationClient
        .getCompanyRegistrationsByName(this.name);
    },
    lookupCompanyRegistrationsByRegistrationNumber() {
      return billingAutomationClient
        .getCompanyRegistrationsByShopRegistrationNumber(this.registrationNumber);
    },
    openCheckout() {
      window.addEventListener('message', this.handleMessageReceived);
      // 'this' unavailable inside openCheckout function
      const vueComponent = this;
      try {
        this.cbInstance.openCheckout({
          hostedPage() {
            return billingAutomationClient.createCheckoutByChargebeeIdAndBillingEntity(
              vueComponent.chargebeeId,
              vueComponent.quote.billing_entity,
              { quoteId: vueComponent.quote.id },
            );
          },
          close() {
            window.removeEventListener('message', vueComponent.handleMessageReceived);
          },
          error(error) {
            const errorMessage = 'Chargebee: error while adding payment method';
            captureException({ errorMessage, error });
            console.error(errorMessage, error);
            vueComponent.showCheckoutOpenErrorToast();
          },
        });
      } catch (error) {
        this.cbInstance.closeAll();
        this.showCheckoutOpenErrorToast();
      }
    },
    showCheckoutOpenErrorToast() {
      this.makeAlertToast(this.$t('fill_billing_info.billing_line.checkout_open_error'));
    },
    handleMessageReceived(evt) {
      // evt.data is either Object, or a simple name string
      if (evt?.data?.key !== 'cb.payment_source.add') return;

      if (evt?.data?.status === 'error') {
        this.hasAddedPayment = false;
        this.showBillingErrorToast();
      } else {
        this.hasAddedPayment = true;
      }
    },
    showBillingErrorToast() {
      this.makeAlertToast(this.$t('fill_billing_info.billing_line.billing_error'));
    },
    validateIban() {
      this.validateMandatoryField('iban');
      if (this.iban && !isValidIban(this.iban)) {
        this.ibanErrorMessage = this.$t('fill_billing_info.billing_line.iban_invalid');
      }
    },
    autofillBillingInfos(company) {
      this.name = company.name;
      this.registrationNumber = company.shopRegistrationNumber;
      this.address = company.address;
      this.city = company.city;
      this.zipcode = company.zipCode;
      this.clearSuggestedCompanyRegistrations();
    },
    validateEmail() {
      this.validateMandatoryField('email');
      if (this.email && !isValidEmail(this.email)) {
        this.emailErrorMessage = this.$t('fill_billing_info.billing_line.email_invalid');
      }
    },
    resolvePrice(resolvedMaxUsers) {
      const price = this.quote.totals.estimated_totals_by_users[resolvedMaxUsers].total_amount;
      return extractPrice(this.quote, price);
    },
    validateMaxUsers() {
      if (this.maxUsers < 0) {
        this.maxUsersErrorMessage = this.$t('quote_details.errors.invalid_max_users');
      } else {
        const priceBookProduct = this.quote.pricebookResponse.pricebook.products.find(
          pr => pr.name === this.lineItemsInfos.title,
        ) || {};
        const maxUsersRanges = (priceBookProduct.prices || []).map(price => price.max_users);
        const maxAllowedUsers =
          maxUsersRanges.length ? Math.max(...maxUsersRanges) : MAX_USERS_ALLOWED;

        if (this.maxUsers > Math.min(maxAllowedUsers, MAX_USERS_ALLOWED)) {
          this.maxUsersErrorMessage = this.$t('quote_details.errors.max_users_exceeded', { max_users: maxAllowedUsers });
          return;
        }

        this.validateNumberTypeFieldPresence('maxUsers');
      }
    },
    validateNumberTypeFieldPresence(attribute) {
      const value = this[attribute];
      if (!value) {
        this[`${attribute}ErrorMessage`] = this.$t('fill_billing_info.billing_line.error_input_mandatory');
      } else {
        this[`${attribute}ErrorMessage`] = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.shop-details-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-block: 24px;
}

.shop-details-form__general-informations {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.shop-details-form__general-informations__inputs-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  &:has(> :nth-child(odd):last-child)>:last-child {
    grid-column: span 2;
  }
}

.shop-details-form__billing-address {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.shop-details-form__billing-address__inputs-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

.shop-details-form__billing-address__inputs-grid__address,
.shop-details-form__billing-address__inputs-grid__sdi {
  grid-column: span 3;
}

.shop-details-form__payment-informations {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.shop-details-form__payment-informations__header {
  display: flex;
  align-items: center;
  gap: 16px;
}

.shop-details-form__payment-informations__bank-transfer {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  flex-direction: column;
}

.shop-details-form__payment-informations__bank-label {
  padding: 8px;
  border-radius: 4px;
  border: 1px solid $sk-grey-10;
}

.shop-details-form__payment-informations__payment-method {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

.shop-details-form__payment-informations__iban {
  display: flex;
  width: 100%;
  gap: 16px;
}

.shop-details-form__secure-payment-label {
  display: flex;
  align-items: center;
  gap: 4px;
  color: $sk-blue-50;
}

.shop-details-form__announced-users-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
</style>
